import React from 'react';
import PropTypes from 'prop-types';
import {
	Col, Table,
	ButtonGroup, Button,
	Form, FormGroup, Label, Input
} from 'reactstrap';

import ConfirmationModal from 'components/utils/confirmation_modal';
import InlineEdit from 'components/utils/inline_edit';
import Week from 'components/utils/week';

import {
	fetchHolidayTimeSlotsOfSchoolYear,
	fetchHolidaySlotGroupsOfSchoolYear,
	addHolidaySlotGroupToSchoolYear
} from 'actions/school_year';
import { updateHolidaySlotGroup, removeHolidaySlotGroup } from 'actions/holiday_slot_group';

import { dayNames } from 'services/calendar';

class AdminSchoolYearDetailHolidayOffers extends React.Component {
	state = {
		dataReady: false,
		holidayTimeSlotsByDay: [],
		holidaySlotGroups: [],
		selectedGroupId: null,
		newGroupProperties: {
			title: '',
			rate: 0,
			nonCAFIncrease: 0
		},
		newGroupHolidayTimeSlotsId: [],
		confirmationModalOpen: false,
		weekConfig: { startTime: 8, endTime: 18, hourHeight: 60 }
	};

	toggleGroup = (groupId) => {
		this.setState((prevState) => {
			const selectedGroupId = groupId === prevState.selectedGroupId ? null : groupId;
			const selectedSlotsId = selectedGroupId ?
				this.state.holidaySlotGroups
				.find((g) => g.id === selectedGroupId)
				.HolidayTimeSlots
				.map((hts) => hts.id)
				: [];

			return {
				selectedGroupId,
				holidayTimeSlotsByDay: prevState.holidayTimeSlotsByDay.map((htsOfDay) => ({
					...htsOfDay,
					slots: htsOfDay.slots.map((slot) => ({ ...slot, selected: selectedSlotsId.includes(slot.id) }))
				}))
			};
		});
	};

	handleGroupEdited = (groupId, changes) => {
		return updateHolidaySlotGroup(groupId, changes)
		.then((group) => {
			this.setState((prevState) => ({
				holidaySlotGroups: prevState.holidaySlotGroups.map((g) => g.id === groupId ? { ...g, ...group } : g)
			}));
		});
	};

	toggleHolidayTimeSlot = (holidayTimeSlotId) => {
		this.setState((prevState) => ({
			holidayTimeSlotsByDay: prevState.holidayTimeSlotsByDay.map((htsOfDay) => ({
				...htsOfDay,
				slots: htsOfDay.slots.map((slot) => ({ ...slot, selected: slot.id === holidayTimeSlotId ? !slot.selected : slot.selected }))
			})),
			newGroupHolidayTimeSlotsId: prevState.newGroupHolidayTimeSlotsId.includes(holidayTimeSlotId) ?
				prevState.newGroupHolidayTimeSlotsId.filter((id) => id !== holidayTimeSlotId) :
				[ ...prevState.newGroupHolidayTimeSlotsId, holidayTimeSlotId ]
		}));
	};

	handleNewGroupPropertyChange = (value, prop) => {
		this.setState((prevState) => ({
			newGroupProperties: { ...prevState.newGroupProperties, [prop]: value }
		}));
	};

	addGroup = (event) => {
		event.preventDefault();
		addHolidaySlotGroupToSchoolYear(
			this.props.schoolYear.id,
			{
				title: this.state.newGroupProperties.title,
				rate: this.state.newGroupProperties.rate * 100,
				nonCAFIncrease: this.state.newGroupProperties.nonCAFIncrease * 100,
				holidayTimeSlots: this.state.newGroupHolidayTimeSlotsId
			}
		)
		.then((group) => {
			this.setState((prevState) => ({
				holidaySlotGroups: [ ...prevState.holidaySlotGroups, group ],
				selectedGroupId: null,
				holidayTimeSlotsByDay: prevState.holidayTimeSlotsByDay.map((htsOfDay) => ({
					...htsOfDay,
					slots: htsOfDay.slots.map((slot) => ({ ...slot, selected: false }))
				})),
				newGroupProperties: { title: '', rate: 0, nonCAFIncrease: 0 },
				newGroupHolidayTimeSlotsId: []
			}));
		});
	};

	removeGroup = () => {
		this.setState({ confirmationModalOpen: true });
	};

	confirmRemoveGroup = () => {
		removeHolidaySlotGroup(this.state.selectedGroupId)
		.then(() => {
			this.setState((prevState) => ({
				holidaySlotGroups: prevState.holidaySlotGroups.filter((g) => g.id !== prevState.selectedGroupId),
				selectedGroupId: null,
				holidayTimeSlotsByDay: prevState.holidayTimeSlotsByDay.map((htsOfDay) => ({
					...htsOfDay,
					slots: htsOfDay.slots.map((slot) => ({ ...slot, selected: false }))
				})),
				confirmationModalOpen: false
			}));
		});
	};

	dismissRemoveGroup = () => {
		this.setState({ confirmationModalOpen: false });
	};

	componentDidMount() {
		Promise.all([
			fetchHolidayTimeSlotsOfSchoolYear(this.props.schoolYear.id),
			fetchHolidaySlotGroupsOfSchoolYear(this.props.schoolYear.id)
		])
		.then(([ holidayTimeSlots, holidaySlotGroups ]) => {
			this.setState({
				holidayTimeSlotsByDay: [0,1,2,3,4].map((i) => ({
					name: dayNames[i],
					slots: holidayTimeSlots.filter((hts) => hts.weekday === i).map((slot) => ({ ...slot, active: true, selected: false }))
				})),
				holidaySlotGroups,
				dataReady: true
			});
		});
	}

	render() {
		const selectedGroup = this.state.holidaySlotGroups.find((g) => g.id === this.state.selectedGroupId);

		return (
			!this.state.dataReady ?
			'Loading...' :
			<div className="text-center">
				<ButtonGroup className="mb-2">
					{ this.state.holidaySlotGroups.map((group) => {
						const active = this.state.selectedGroupId === group.id;
						return (
							<Button key={ group.id } color={ active ? 'success' : 'info' } active={ active } style={{ cursor: 'pointer' }} onClick={ () => this.toggleGroup(group.id) }>
								{ group.title }
							</Button>
						);
					}) }
				</ButtonGroup>

				{ this.state.selectedGroupId && selectedGroup &&
				<React.Fragment>
				<Table className="text-left" key={ selectedGroup.id }>
					<tbody>
						<tr>
							<th scope="row" style={{ width: '25%'}}>Title</th>
							<td><InlineEdit type="text" value={ selectedGroup.title } onEdited={ (title) => this.handleGroupEdited(selectedGroup.id, { title }) } /></td>
						</tr>
						<tr>
							<th scope="row" style={{ width: '25%'}}>Rate</th>
							<td><InlineEdit type="number" value={ selectedGroup.rate/100 } onEdited={ (rate) => this.handleGroupEdited(selectedGroup.id, { rate: rate*100 }) } /></td>
						</tr>
						<tr>
							<th scope="row" style={{ width: '25%'}}>Non-CAF increase</th>
							<td><InlineEdit type="number" value={ selectedGroup.nonCAFIncrease/100 } onEdited={ (nonCAFIncrease) => this.handleGroupEdited(selectedGroup.id, { nonCAFIncrease: nonCAFIncrease*100 }) } /></td>
						</tr>
					</tbody>
				</Table>
				<Button color="danger" className="mb-2" style={{ cursor: 'pointer' }} onClick={ this.removeGroup }>
					<i className="fa fa-trash-o"></i>Remove
				</Button>
				<ConfirmationModal
					isOpen={ this.state.confirmationModalOpen }
					text={ 'Are you sure you want to remove this holiday offer (' + selectedGroup.title +') ?' }
					onConfirm={ this.confirmRemoveGroup }
					onDismiss={ this.dismissRemoveGroup }
				/>
				</React.Fragment> }

				{ !this.state.selectedGroupId &&
				<Form onSubmit={ this.addGroup }>
					<FormGroup row>
						<Label for="title" sm={2}>Title</Label>
						<Col sm={10}>
							<Input type="text" required value={ this.state.newGroupProperties.title } onChange={ (e) => this.handleNewGroupPropertyChange(e.target.value, 'title') } name="title" id="title" placeholder="Title" />
						</Col>
					</FormGroup>
					<FormGroup row>
						<Label for="rate" sm={2}>Rate</Label>
						<Col sm={10}>
							<Input type="number" value={ this.state.newGroupProperties.rate } onChange={ (e) => this.handleNewGroupPropertyChange(parseInt(e.target.value, 10) || 0, 'rate') } name="rate" id="rate" />
						</Col>
					</FormGroup>
					<FormGroup row>
						<Label for="nonCAFIncrease" sm={2}>Non-CAF increase</Label>
						<Col sm={10}>
							<Input type="number" value={ this.state.newGroupProperties.nonCAFIncrease } onChange={ (e) => this.handleNewGroupPropertyChange(parseInt(e.target.value, 10) || 0, 'nonCAFIncrease') } name="nonCAFIncrease" id="nonCAFIncrease" />
						</Col>
					</FormGroup>
					<FormGroup row style={{ justifyContent: 'center' }}>
						<Button color="primary" style={{ cursor: 'pointer' }} disabled={ this.state.newGroupHolidayTimeSlotsId.length === 0 }>
							<i className="fa fa-plus"></i> Add
						</Button>
					</FormGroup>
				</Form> }

				<Week
					config={ this.state.weekConfig }
					slotsByDay={ this.state.holidayTimeSlotsByDay }
					onToggleSlot={ !this.state.selectedGroupId ? this.toggleHolidayTimeSlot : null }
				/>
			</div>
		);
	}
};

AdminSchoolYearDetailHolidayOffers.propTypes = {
	schoolYear: PropTypes.object.isRequired
};

export default AdminSchoolYearDetailHolidayOffers;
